package com.htmlagent.site.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.htmlagent.site.theme.InlineCodeStyle
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Text

/**
 * The agent's text with the little Markdown it writes: bold, italic and code.
 *
 * Nodes are built, never markup strings: the text was written by a model, and through
 * `innerHTML` an `<img onerror=…>` would run in the application, not in the isolated iframe.
 * No links, on purpose: they would be a URL chosen by the model, clickable in the user's
 * session.
 */
@Composable
fun RichText(text: String, modifier: Modifier = Modifier) {
    Div(modifier.toAttrs()) {
        parseInline(text).forEach { piece ->
            when (piece) {
                is Piece.Plain -> Text(piece.text)
                is Piece.Strong -> B { Text(piece.text) }
                is Piece.Emphasis -> Em { Text(piece.text) }
                is Piece.Code -> Code(InlineCodeStyle.toModifier().toAttrs()) { Text(piece.text) }
            }
        }
    }
}

/** A piece of text with its role. */
sealed interface Piece {
    val text: String

    data class Plain(override val text: String) : Piece
    data class Strong(override val text: String) : Piece
    data class Emphasis(override val text: String) : Piece
    data class Code(override val text: String) : Piece
}

/**
 * Splits the text into pieces with a role.
 *
 * An unpaired marker is plain text: a stray `**` in "2 ** 3" shows as is and does not eat the
 * rest. `**` comes before `*`: the other way round bold would read as two empty italics.
 */
fun parseInline(text: String): List<Piece> {
    val pieces = mutableListOf<Piece>()
    val plain = StringBuilder()
    var i = 0

    fun flushPlain() {
        if (plain.isNotEmpty()) {
            pieces += Piece.Plain(plain.toString())
            plain.setLength(0)
        }
    }

    while (i < text.length) {
        val mark = MARKS.firstOrNull { text.startsWith(it.open, i) }
        val closeIndex = mark?.let { text.indexOf(it.close, i + it.open.length) }

        if (mark == null || closeIndex == null || closeIndex == i + mark.open.length) {
            // No marker, no closer, or empty (`****`): it is text and gets copied as is.
            plain.append(text[i])
            i++
            continue
        }

        flushPlain()
        pieces += mark.build(text.substring(i + mark.open.length, closeIndex))
        i = closeIndex + mark.close.length
    }

    flushPlain()
    return pieces
}

private class Mark(val open: String, val close: String, val build: (String) -> Piece)

private val MARKS = listOf(
    Mark("**", "**") { Piece.Strong(it) },
    Mark("`", "`") { Piece.Code(it) },
    Mark("*", "*") { Piece.Emphasis(it) },
)
